import {
  ORGANISATIONS,
  type Organisation,
  SIGNS,
  type SignGroup,
  offeredOrganisation,
} from '@orgsim/shared';
import { useState } from 'react';
import { create } from 'zustand';
import { ORGANISATION_LABELS } from '../ui/labels';
import { signSvg } from './svg';

const GROUP_LABELS: Record<SignGroup, string> = {
  vehicle: 'Fahrzeuge',
  command: 'Führung',
  area: 'Räume',
};

// Carrier for newly placed vehicles, read by the placing screen when the map is clicked.
// Undefined means each sign's own default (ELW red, rescue vehicles white).
export const usePaletteOrganisation = create<{
  organisation: Organisation | undefined;
  setOrganisation: (organisation: Organisation | undefined) => void;
}>((set) => ({
  organisation: undefined,
  setOrganisation: (organisation) => set({ organisation }),
}));

// The palette's carrier for a new sign of this type, if one was chosen and the sign offers it.
export function placementOrganisation(signType: string): Organisation | undefined {
  return offeredOrganisation(signType, usePaletteOrganisation.getState().organisation);
}

type Props = {
  selected: string | null;
  onSelect: (signType: string | null) => void;
  compact?: boolean;
};

export function SignPalette({ selected, onSelect, compact = false }: Props) {
  const groups: SignGroup[] = ['vehicle', 'command', 'area'];
  const [group, setGroup] = useState<SignGroup>('vehicle');
  const { organisation, setOrganisation } = usePaletteOrganisation();
  const shown = compact ? groups : [group];
  const hasCarrier = SIGNS.some((s) => shown.includes(s.group) && s.organisations);
  return (
    <div className={compact ? 'compact-palette' : 'sign-palette'}>
      {!compact ? (
        <div className="palette-tabs" aria-label="Zeichengruppe">
          {groups.map((g) => (
            <button key={g} type="button" aria-pressed={group === g} onClick={() => setGroup(g)}>
              {GROUP_LABELS[g]}
            </button>
          ))}
        </div>
      ) : null}
      {hasCarrier ? (
        <div className="palette-carrier" aria-label="Träger">
          <span>Träger:</span>
          {[undefined, ...ORGANISATIONS].map((o) => (
            <button
              key={o ?? 'default'}
              type="button"
              aria-pressed={organisation === o}
              onClick={() => setOrganisation(o)}
            >
              {o ? ORGANISATION_LABELS[o] : 'Standard'}
            </button>
          ))}
        </div>
      ) : null}
      {shown.map((g) => (
        <div key={g}>
          <div className={compact ? 'flex gap-1' : 'sign-grid'}>
            {SIGNS.filter((s) => s.group === g && !s.hidden).map((s) => (
              <button
                key={s.id}
                type="button"
                title={s.label}
                aria-label={s.label}
                aria-pressed={selected === s.id}
                onClick={() => onSelect(selected === s.id ? null : s.id)}
                className={`sign-option ${selected === s.id ? 'selected' : ''}`}
              >
                <span
                  aria-hidden="true"
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: our own SVG strings
                  dangerouslySetInnerHTML={{ __html: signSvg(s.id, { size: 32, organisation }) }}
                />
                {!compact ? <span>{s.label}</span> : null}
              </button>
            ))}
          </div>
        </div>
      ))}
      {selected ? (
        <div className="placement-help">
          Auf die Karte klicken, um zu platzieren. Esc bricht ab.
        </div>
      ) : null}
    </div>
  );
}
